// Shared, pure helpers for habitats (no Pixi imports so they stay unit-testable).
import type { AmbienceInput } from "./types";

export const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Exponential smoothing factor for a time constant `tau` seconds over `dt`. */
export const smoothK = (dt: number, tau: number) => 1 - Math.exp(-dt / Math.max(1e-4, tau));

/**
 * Smoothed ambience envelope: attack fast, release slow. `level` is 0..1 loudness,
 * `pulse` is a 0..1 decaying flash at each beat onset (scaled by peak).
 */
export class AmbienceFollower {
  level = 0;
  peak = 0;
  pulse = 0;
  private lastBeat = 0;

  update(dt: number, amb: AmbienceInput): void {
    // RMS of a typical mix sits around 0.1..0.4, so stretch it.
    const target = clamp01(amb.masterRms * 2.5);
    const k = target > this.level ? smoothK(dt, 0.05) : smoothK(dt, 0.6);
    this.level += (target - this.level) * k;
    const pk = clamp01(amb.masterPeak);
    this.peak = pk > this.peak ? pk : this.peak + (pk - this.peak) * smoothK(dt, 0.35);
    // Beat onset = beatPhase wrapped around.
    if (amb.playing && amb.beatPhase < this.lastBeat - 0.5) {
      this.pulse = Math.max(this.pulse, 0.35 + 0.65 * this.peak);
    }
    this.lastBeat = amb.beatPhase;
    this.pulse *= Math.exp(-dt * 6);
  }
}

/** Time-of-day sky stops (top, horizon) as 0xRRGGBB. */
export type TimeOfDay = "dawn" | "day" | "dusk" | "night";

/** Darkness factor 0 (bright day) .. 1 (night). */
export function nightness(t: TimeOfDay): number {
  return t === "day" ? 0 : t === "dawn" ? 0.3 : t === "dusk" ? 0.45 : 1;
}
